import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Target, Users, LineChart, ShieldCheck } from 'lucide-react';
import RfPageFrame from '../layout/RfPageFrame';

const items = [
  {
    to: '/services/ai-security',
    title: 'AI Security',
    desc: 'Comprehensive assessment and risk programmes for generative AI — including ScanFort (red teaming) and RiskFort (risk management).',
    icon: Shield,
    color: 'text-blue-600'
  },
  {
    to: '/services/human-trust',
    title: 'Human Trust Services',
    desc: 'AI-era human risk management: SimStrike multi-vector attack simulations and the AI Trust & Readiness Programme — covering awareness training, trust architecture, policy & compliance, and security culture.',
    icon: ShieldCheck,
    color: 'text-emerald-600'
  },
  {
    to: '/services/ai-consulting',
    title: 'AI Consulting',
    desc: 'Strategic advisory on AI adoption, use cases, and responsible governance frameworks.',
    icon: Target,
    color: 'text-yellow-600'
  },
  {
    to: '/services/risk-management',
    title: 'Risk Management',
    desc: 'Enterprise risk framing and threat modeling for AI and digital transformation initiatives.',
    icon: LineChart,
    color: 'text-purple-600'
  },
  {
    to: '/services/human-resources',
    title: 'Human Resources',
    desc: 'People readiness, skills analysis, and organizational change management for AI-enabled teams.',
    icon: Users,
    color: 'text-orange-600'
  },
];

export default function ServicesHub() {
  return (
    <>
      <div className="rf-hero-band">
        <RfPageFrame className="!py-10 md:!py-16 text-center">
          <p className="text-xs font-medium uppercase tracking-[0.15em] text-[#5c6d7e] mb-3">Service Directory</p>
          <h1 className="text-3xl md:text-[2.5rem] font-bold text-[#2c2c2c] tracking-tight max-w-3xl mx-auto leading-tight">
            How we work with your organisation
          </h1>
          <p className="mt-6 text-[1.1rem] text-[#5c5c5c] max-w-2xl mx-auto leading-relaxed">
            A structured portfolio of services designed to secure, govern, and accelerate your AI initiatives. 
            Choose a service line to explore our specific methodologies and engagement models.
          </p>
        </RfPageFrame>
      </div>

      <section className="bg-[#f0f7ff]">
        <RfPageFrame className="pb-24 pt-10">
          <div className="grid gap-6">
            {items.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="rf-card block p-8 hover:border-[#005a9c]/40 group transition-all"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  <div className={`w-14 h-14 rounded-xl bg-slate-50 flex items-center justify-center shrink-0 border border-slate-100 group-hover:bg-white group-hover:shadow-sm transition-all`}>
                    <item.icon className={`w-7 h-7 ${item.color}`} />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-[#2c2c2c] group-hover:text-[#005a9c] transition-colors mb-2">{item.title}</h2>
                    <p className="text-[1rem] text-[#5c5c5c] leading-relaxed max-w-3xl">{item.desc}</p>
                  </div>
                  <div className="hidden md:block">
                    <div className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center text-slate-400 group-hover:border-[#005a9c] group-hover:text-[#005a9c] transition-all">
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-16 p-10 bg-white rounded-2xl border border-[#005a9c]/10 text-center shadow-sm">
            <h3 className="text-xl font-semibold text-[#2c2c2c] mb-3">Custom Engagements</h3>
            <p className="text-[#5c5c5c] mb-8 max-w-2xl mx-auto">
              Need a tailored approach that combines multiple service lines? Our team can design a bespoke 
              statement of work to meet your specific security and governance requirements.
            </p>
            <Link
              to="/contactus"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#005a9c] text-white font-medium rounded-lg hover:bg-[#003d6b] transition-colors shadow-md"
            >
              Schedule a consultation
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </RfPageFrame>
      </section>
    </>
  );
}
